import { useRef } from 'react'

import { createStyles } from 'antd-style'

import { useCreationForm } from '@eastcoal/formily/hooks/useCreationForm'

import { GenericalRecord } from '../../types/utilities'

import { RemoteTableProps } from './interface'
import { RemoteTableSearchForm } from './SearchForm'
import { RemoteTableStoreProvider } from './Store'
import { RemoteTableCore } from './TableCore'
import { Toolbar } from './Toolbar'

const useStyles = createStyles(({ token }) => ({
  container: {
    ['&:fullscreen']: {
      paddingBlock: token.paddingSM,
      backgroundColor: token.colorWhite,
      overflow: 'auto'
    }
  }
}))

export const RemoteTable = <T extends GenericalRecord, P, S>({
  ref,
  className,
  style,
  styles: externalStyles,

  /* -------------------------------------------------------------------------- */
  /*                            RemoteTableStore Props                            */
  /* -------------------------------------------------------------------------- */
  api,
  manualRequest = false,
  readyRequest = true,
  requestParams: requestParams,
  refreshDeps,
  defaultCurrent,
  defaultPageSize,

  /* -------------------------------------------------------------------------- */
  /*                                   自定义顶部栏                                   */
  /* -------------------------------------------------------------------------- */
  topbar,

  /* -------------------------------------------------------------------------- */
  /*                             SearchForm 属性                            */
  /* -------------------------------------------------------------------------- */
  searchForm,

  /* -------------------------------------------------------------------------- */
  /*                                 Toolbar 属性                                */
  /* -------------------------------------------------------------------------- */
  toolbar = false,

  /* -------------------------------------------------------------------------- */
  /*                                TableCore 属性                               */
  /* -------------------------------------------------------------------------- */
  rowKey,
  pagination,
  ...tableProps
}: RemoteTableProps<T, P, S>) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const tableSearchForm = useCreationForm()

  const { cx, styles } = useStyles()

  return (
    <RemoteTableStoreProvider
      ref={ref}
      api={api}
      containerRef={containerRef}
      manualRequest={manualRequest}
      readyRequest={readyRequest}
      refreshDeps={refreshDeps}
      requestParams={requestParams}
      rowKey={rowKey}
      searchForm={searchForm?.form}
      defaultCurrent={
        defaultCurrent ?? ((pagination && pagination.current) || undefined)
      }
      defaultPageSize={
        defaultPageSize ?? ((pagination && pagination.pageSize) || undefined)
      }
    >
      <div
        ref={containerRef}
        className={cx(styles.container, className, 'remote-table-container')}
        style={style}
      >
        {topbar}
        {searchForm && (
          <RemoteTableSearchForm
            style={externalStyles?.searchForm}
            {...searchForm}
            form={tableSearchForm}
          />
        )}
        {toolbar !== false && (
          <Toolbar style={externalStyles?.toolbar} {...toolbar} />
        )}
        <RemoteTableCore
          pagination={pagination}
          rowKey={rowKey}
          {...tableProps}
        />
      </div>
    </RemoteTableStoreProvider>
  )
}
